import React, { useState } from 'react';
import { ApiService } from '../../services/ApiService';
import { Form } from 'models/forms/Form';
import FormList from 'components/forms/FormList';
import { useParams } from 'react-router-dom';
import type { NodeTemplate } from 'models/nodes/NodeTemplate';
import { NodeType } from 'models/nodes/NodeType';
import { EnrollmentType } from 'models/enrollments/EnrollmentType';
import type { WorkflowTemplate } from 'models/workflows/WorkflowTemplate';
import type { EdgeTemplate } from 'models/workflows/EdgeTemplate';
import { utilities } from 'utils/utilities';
import EdgeConditionBuilder from 'components/workflows/EdgeConditionBuilder';
import { getNodeConditionFields } from 'utils/nodeConditionFields';
import { serializeCondition } from 'models/workflows/ConditionRule';
import type { ConditionRule } from 'models/workflows/ConditionRule';
import { summarizeCondition } from 'utils/conditionSummary';
import type { ConditionFieldDefinition } from 'models/workflows/ConditionField';

interface WorkflowCreatorProps {
  existingTemplate: WorkflowTemplate | null;
  ownerId: string;
}

const nodeTypeEnrollmentMap: Record<NodeType, EnrollmentType> = {
  [NodeType.Form]: EnrollmentType.FormEnrollment,
  [NodeType.TeamReview]: EnrollmentType.ReviewEnrollment,
  [NodeType.AutomatedAction]: EnrollmentType.EventEnrollment,
  [NodeType.Consultation]: EnrollmentType.ConsultationEnrollment,
  [NodeType.SecurityDeposit]: EnrollmentType.SecurityDepositEnrollment,
  [NodeType.Invoice]: EnrollmentType.InvoiceEnrollment,
};

const nodeTypeSubmissionMap: Record<NodeType, string> = {
  [NodeType.Form]: 'FormSubmission',
  [NodeType.TeamReview]: 'ReviewDecision',
  [NodeType.AutomatedAction]: 'EventSubmission',
  [NodeType.Consultation]: 'ConsultationSubmission',
  [NodeType.SecurityDeposit]: 'SecurityDepositSubmission',
  [NodeType.Invoice]: 'InvoiceSubmission',
};

// Used to create new workflow templates and edit them.
const WorkflowCreator: React.FC<WorkflowCreatorProps> = ({ existingTemplate, ownerId }) => {
  const [nodeTemplates, setNodeTemplates] = useState<NodeTemplate[]>(
    (existingTemplate?.nodeTemplates ?? []).map((n) => ({
      ...n,
      configurationJson: n.configurationJson ?? '{}',
    }))
  );
  const [workflowName, setWorkflowName] = useState<string>(existingTemplate?.name ?? '');
  const [isActive, setIsActive] = useState<boolean>(existingTemplate?.isActive ?? false);
  const [workflowLink, setWorkflowLink] = useState<string>(existingTemplate?.workflowLink ?? '');
  const [edgeTemplates, setEdgeTemplates] = useState<EdgeTemplate[]>(
    existingTemplate?.edgeTemplates ?? []
  );
  const [newEdgeFrom, setNewEdgeFrom] = useState<string>('');
  const [newEdgeTo, setNewEdgeTo] = useState<string>('');
  const [newEdgeName, setNewEdgeName] = useState<string>('');
  const [newEdgePriority, setNewEdgePriority] = useState<number>(0);
  const [newEdgeCondition, setNewEdgeCondition] = useState<ConditionRule | null>(null);
  const [conditionFields, setConditionFields] = useState<ConditionFieldDefinition[]>([]);
  const [conditionFieldsLoading, setConditionFieldsLoading] = useState<boolean>(false);
  const [editingEdgeId, setEditingEdgeId] = useState<string | null>(null);
  const [editFields, setEditFields] = useState<{
    from: string;
    to: string;
    name: string;
    priority: number;
    conditionJson: string;
  }>({ from: '', to: '', name: '', priority: 0, conditionJson: '' });
  const [stepsCollapsed, setStepsCollapsed] = useState<boolean>(false);
  const [addStepCollapsed, setAddStepCollapsed] = useState<boolean>(false);
  const [edgesCollapsed, setEdgesCollapsed] = useState<boolean>(false);
  const [edgeHelpOpen, setEdgeHelpOpen] = useState<boolean>(false);
  const [workflowHelpOpen, setWorkflowHelpOpen] = useState<boolean>(false);
  const [consultationCollapsed, setConsultationCollapsed] = useState<boolean>(false);
  const [consultationType, setConsultationType] = useState<'Physical' | 'Online'>('Physical');
  const [consultationAddress, setConsultationAddress] = useState<string>('');
  const [consultationProvider, setConsultationProvider] = useState<string>('GoogleMeet');
  const [depositCollapsed, setDepositCollapsed] = useState<boolean>(false);
  const [invoiceCollapsed, setInvoiceCollapsed] = useState<boolean>(false);
  const { teamId } = useParams<{ teamId: string }>();

  if (!teamId) return null;

  const nextOrder = nodeTemplates.length + 1;

  // Nodes that have real server-assigned IDs (not DEFAULT_GUID) — eligible for edge endpoints
  const assignedNodes = nodeTemplates.filter((n) => n.id !== utilities.DEFAULT_GUID);

  const handleAddEdge = () => {
    if (!newEdgeFrom || !newEdgeTo || newEdgeFrom === newEdgeTo) return;
    const edge: EdgeTemplate = {
      id: crypto.randomUUID(),
      workflowTemplateId: existingTemplate?.id ?? utilities.DEFAULT_GUID,
      fromNodeTemplateId: newEdgeFrom,
      toNodeTemplateId: newEdgeTo,
      name: newEdgeName.trim() || undefined,
      priority: newEdgePriority,
      conditionJson: newEdgeCondition ? serializeCondition(newEdgeCondition) : undefined,
    };
    setEdgeTemplates((prev) => [...prev, edge]);
    setNewEdgeFrom('');
    setNewEdgeTo('');
    setNewEdgeName('');
    setNewEdgePriority(0);
    setNewEdgeCondition(null);
    setConditionFields([]);
  };

  const handleDeleteEdge = (id: string) => {
    setEdgeTemplates((prev) => prev.filter((e) => e.id !== id));
  };

  const handleFromStepChange = async (nodeId: string) => {
    setNewEdgeFrom(nodeId);
    setNewEdgeCondition(null);
    setConditionFields([]);
    if (!nodeId) return;
    const selectedNode = assignedNodes.find((n) => n.id === nodeId);
    if (!selectedNode) return;
    setConditionFieldsLoading(true);
    try {
      const fields = await getNodeConditionFields(selectedNode, teamId);
      setConditionFields(fields);
    } catch {
      setConditionFields([]);
    } finally {
      setConditionFieldsLoading(false);
    }
  };

  const handleEditStart = (edge: EdgeTemplate) => {
    setEditingEdgeId(edge.id);
    setEditFields({
      from: edge.fromNodeTemplateId,
      to: edge.toNodeTemplateId,
      name: edge.name ?? '',
      priority: edge.priority,
      conditionJson: edge.conditionJson ?? '',
    });
  };

  const handleEditSave = (id: string) => {
    setEdgeTemplates((prev) =>
      prev.map((e) =>
        e.id === id
          ? {
              ...e,
              fromNodeTemplateId: editFields.from,
              toNodeTemplateId: editFields.to,
              name: editFields.name.trim() || undefined,
              priority: editFields.priority,
              conditionJson: editFields.conditionJson.trim() || undefined,
            }
          : e
      )
    );
    setEditingEdgeId(null);
  };

  const handleEditCancel = () => {
    setEditingEdgeId(null);
  };

  const addNodeTemplate = (partial: Omit<NodeTemplate, 'id' | 'order'>) => {
    const template: NodeTemplate = {
      id: utilities.DEFAULT_GUID,
      order: nextOrder,
      ...partial,
    };
    setNodeTemplates((prev) => [...prev, template]);
  };

  // Form step — triggered by FormList picker
  const handleFormClick = (formId: string | Form) => {
    const resolvedFormId = typeof formId === 'string' ? formId : formId.id;
    addNodeTemplate({
      nodeType: NodeType.Form,
      enrollmentType: nodeTypeEnrollmentMap[NodeType.Form],
      requiredSubmissionType: nodeTypeSubmissionMap[NodeType.Form],
      configurationJson: JSON.stringify({ formId: resolvedFormId }),
    });
  };

  // TeamReview step
  const handleAddTeamReviewStep = async () => {
    let teamReviewSettingsId = '';
    try {
      const response = await ApiService.getTeamReviewSettings(teamId, '');
      const settings = Array.isArray(response.data) ? response.data[0] : response.data;
      teamReviewSettingsId = settings?.teamReviewSettingsId ?? '';
    } catch {
      console.warn('Could not fetch team review settings; teamReviewSettingsId will be empty.');
    }
    addNodeTemplate({
      nodeType: NodeType.TeamReview,
      enrollmentType: nodeTypeEnrollmentMap[NodeType.TeamReview],
      requiredSubmissionType: nodeTypeSubmissionMap[NodeType.TeamReview],
      configurationJson: JSON.stringify({ assignedTeamId: teamId, teamReviewSettingsId }),
    });
  };

  // AutomatedAction step
  const handleAddAutomatedActionStep = () => {
    addNodeTemplate({
      nodeType: NodeType.AutomatedAction,
      enrollmentType: nodeTypeEnrollmentMap[NodeType.AutomatedAction],
      requiredSubmissionType: nodeTypeSubmissionMap[NodeType.AutomatedAction],
      configurationJson: '{}',
    });
  };

  // Consultation step
  const handleAddConsultationStep = () => {
    const configurationJson = JSON.stringify({
      consultationType,
      defaultAddress: consultationType === 'Physical' ? consultationAddress : undefined,
      videoProvider: consultationType === 'Online' ? consultationProvider : undefined,
    });
    addNodeTemplate({
      nodeType: NodeType.Consultation,
      enrollmentType: nodeTypeEnrollmentMap[NodeType.Consultation],
      requiredSubmissionType: nodeTypeSubmissionMap[NodeType.Consultation],
      configurationJson,
    });
  };

  // SecurityDeposit step — amounts are set by the team per client at runtime,
  // so the template carries no money-related configuration.
  const handleAddSecurityDepositStep = () => {
    addNodeTemplate({
      nodeType: NodeType.SecurityDeposit,
      enrollmentType: nodeTypeEnrollmentMap[NodeType.SecurityDeposit],
      requiredSubmissionType: nodeTypeSubmissionMap[NodeType.SecurityDeposit],
      configurationJson: '{}',
    });
  };

  // Invoice step — amounts are set by the team per client at runtime,
  // so the template carries no money-related configuration.
  const handleAddInvoiceStep = () => {
    addNodeTemplate({
      nodeType: NodeType.Invoice,
      enrollmentType: nodeTypeEnrollmentMap[NodeType.Invoice],
      requiredSubmissionType: nodeTypeSubmissionMap[NodeType.Invoice],
      configurationJson: '{}',
    });
  };

  const handleDeleteStep = (id: string) => {
    setNodeTemplates((prev) => {
      const filtered = prev.filter((t) => t.id !== id);
      return filtered.map((t, i) => ({ ...t, order: i + 1 }));
    });
  };

  const handleSaveWorkflowClick = async () => {
    const payload = {
      name: workflowName,
      description: '',
      workflowLink,
      isActive,
      nodeTemplates,
      edgeTemplates,
    };

    try {
      if (existingTemplate === null) {
        const response = await ApiService.insertWorkflowTemplate(teamId, { ...payload, teamId, ownerId });
        console.log('Workflow template created:', response);
      } else {
        const response = await ApiService.updateWorkflowTemplate(teamId, existingTemplate.id, payload);
        console.log('Workflow template updated:', response);
        setNodeTemplates(
          (response.nodeTemplates ?? []).map((n) => ({
            ...n,
            configurationJson: n.configurationJson ?? '{}',
          }))
        );
        setEdgeTemplates(response.edgeTemplates ?? []);
      }
    } catch (error) {
      console.error('Failed to save workflow template:', error);
      alert('Failed to save workflow. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="container mx-auto bg-white p-6 rounded-lg shadow-lg">
        <div className="flex items-center gap-2 mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Workflow Creator</h1>
          <button
            type="button"
            onClick={() => setWorkflowHelpOpen((o) => !o)}
            aria-label="What is a workflow?"
            className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-300 text-gray-600 text-xs font-bold hover:bg-blue-100 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            ?
          </button>
        </div>
        {workflowHelpOpen && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-gray-700 space-y-2">
            <p className="font-semibold text-blue-700">What is a Workflow?</p>
            <p>
              A <strong>workflow</strong> is a reusable sequence of steps that a client moves through when they start a project with you. Think of it as a structured checklist that guides both you and your client from start to finish.
            </p>
            <p>
              Each <strong>step</strong> can be a form for the client to fill out, a team review where your team approves or rejects a submission, or an automated action that runs behind the scenes.
            </p>
            <p>
              Once you publish a workflow, clients can be enrolled in it and the system will automatically track which step they're on, what's been submitted, and what still needs to happen.
            </p>
            <p className="text-gray-500 text-xs">
              You can define as many steps as you need and optionally add <strong>Edge Routing</strong> to create branching paths based on decisions made along the way.
            </p>
            <button
              type="button"
              onClick={() => setWorkflowHelpOpen(false)}
              className="mt-1 text-xs font-medium text-blue-600 hover:underline focus:outline-none"
            >
              Got it
            </button>
          </div>
        )}

        {/* Workflow Name */}
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="workflowName">
            Workflow Name:
          </label>
          <input
            type="text"
            id="workflowName"
            value={workflowName}
            onChange={(e) => setWorkflowName(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>

        {/* Active toggle */}
        <div className="mb-4">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={isActive}
              onChange={() => setIsActive(!isActive)}
              className="form-checkbox h-5 w-5 text-blue-600"
            />
            <span className="ml-2 text-gray-700">Enable Workflow</span>
          </label>
        </div>

        {/* Workflow Link */}
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="workflowLink">
            Workflow Link:
            <input
              type="text"
              id="workflowLink"
              value={workflowLink}
              onChange={(e) => setWorkflowLink(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
          </label>
        </div>

        {nodeTemplates.length > 0 && (
          <button
            onClick={handleSaveWorkflowClick}
            className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Save Workflow
          </button>
        )}

        {/* Step list */}
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setStepsCollapsed((c) => !c)}
            className="flex w-full items-center justify-between mb-4 group"
          >
            <h2 className="text-xl font-semibold text-gray-800">Steps</h2>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-transform duration-200 ${stepsCollapsed ? '-rotate-90' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {!stepsCollapsed && (
            nodeTemplates.length === 0 ? (
              <p className="text-gray-500 text-sm">No steps yet. Add steps below.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {nodeTemplates.map((t) => (
                  <div key={t.id} className="border border-gray-300 rounded-lg p-4 bg-white shadow-sm">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Step {t.order}</p>
                        <p className="font-semibold text-gray-800">{t.nodeType}</p>
                        <p className="text-sm text-gray-600">{t.enrollmentType}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteStep(t.id)}
                        className="text-red-400 hover:text-red-600 text-xs ml-2"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        {/* Step builder */}
        <div className="bg-gray-100 p-4 mt-6 rounded-lg">
          <button
            type="button"
            onClick={() => setAddStepCollapsed((c) => !c)}
            className="flex w-full items-center justify-between group"
          >
            <h3 className="text-lg font-semibold text-gray-700">Add a Step</h3>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-transform duration-200 ${addStepCollapsed ? '-rotate-90' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {!addStepCollapsed && (
            <div className="space-y-6 mt-4">
              {/* TeamReview */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Team Review Step</p>
                <button
                  type="button"
                  onClick={handleAddTeamReviewStep}
                  className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Add Team Review
                </button>
              </div>

              {/* AutomatedAction */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Automated Action Step</p>
                <button
                  type="button"
                  onClick={handleAddAutomatedActionStep}
                  className="inline-flex items-center justify-center rounded-lg bg-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Add Automated Action
                </button>
              </div>

              {/* Consultation */}
              <div className="border border-gray-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setConsultationCollapsed((c) => !c)}
                  className="flex w-full items-center justify-between px-4 py-3 group"
                >
                  <p className="text-sm font-medium text-gray-700">Consultation Step</p>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-transform duration-200 ${consultationCollapsed ? '-rotate-90' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {!consultationCollapsed && (
                  <div className="px-4 pb-4 space-y-3">
                    {/* Consultation Type */}
                    <div>
                      <label className="block text-xs text-gray-600 mb-1" htmlFor="consultation-type">
                        Consultation Type
                      </label>
                      <select
                        id="consultation-type"
                        value={consultationType}
                        onChange={(e) => setConsultationType(e.target.value as 'Physical' | 'Online')}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline"
                      >
                        <option value="Physical">Physical</option>
                        <option value="Online">Online</option>
                      </select>
                    </div>

                    {/* Physical — Default Address */}
                    {consultationType === 'Physical' && (
                      <div>
                        <label className="block text-xs text-gray-600 mb-1" htmlFor="consultation-address">
                          Default Address
                        </label>
                        <input
                          id="consultation-address"
                          type="text"
                          value={consultationAddress}
                          onChange={(e) => setConsultationAddress(e.target.value)}
                          placeholder="123 Main St, City, State"
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline"
                        />
                      </div>
                    )}

                    {/* Online — Video Provider */}
                    {consultationType === 'Online' && (
                      <div>
                        <label className="block text-xs text-gray-600 mb-1" htmlFor="consultation-provider">
                          Video Provider
                        </label>
                        <select
                          id="consultation-provider"
                          value={consultationProvider}
                          onChange={(e) => setConsultationProvider(e.target.value)}
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline"
                        >
                          <option value="GoogleMeet">Google Meet</option>
                          <option value="Zoom">Zoom</option>
                          <option value="MicrosoftTeams">Microsoft Teams</option>
                          <option value="Custom">Custom</option>
                        </select>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleAddConsultationStep}
                      className="inline-flex items-center justify-center rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                    >
                      Add Consultation Step
                    </button>
                  </div>
                )}
              </div>

              {/* Security Deposit */}
              <div className="border border-gray-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setDepositCollapsed((c) => !c)}
                  className="flex w-full items-center justify-between px-4 py-3 group"
                >
                  <p className="text-sm font-medium text-gray-700">Security Deposit Step</p>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-transform duration-200 ${depositCollapsed ? '-rotate-90' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {!depositCollapsed && (
                  <div className="px-4 pb-4 space-y-3">
                    <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2.5 text-xs text-blue-700">
                      <svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
                      </svg>
                      <span>
                        The deposit amount is <strong>not</strong> set here. Your team enters it for each
                        client while the workflow is running, once this step becomes active. The client
                        can view and pay only after your team finalizes it.
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddSecurityDepositStep}
                      className="inline-flex items-center justify-center rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                    >
                      Add Deposit Step
                    </button>
                  </div>
                )}
              </div>

              {/* Invoice */}
              <div className="border border-gray-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setInvoiceCollapsed((c) => !c)}
                  className="flex w-full items-center justify-between px-4 py-3 group"
                >
                  <p className="text-sm font-medium text-gray-700">Invoice Step</p>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-transform duration-200 ${invoiceCollapsed ? '-rotate-90' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {!invoiceCollapsed && (
                  <div className="px-4 pb-4 space-y-3">
                    <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2.5 text-xs text-blue-700">
                      <svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
                      </svg>
                      <span>
                        The invoice total is <strong>not</strong> set here. Your team enters it for each
                        client while the workflow is running, once this step becomes active. Any security
                        deposit already paid is automatically subtracted from the invoice total.
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddInvoiceStep}
                      className="inline-flex items-center justify-center rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                    >
                      Add Invoice Step
                    </button>
                  </div>
                )}
              </div>

              {/* Form step via FormList */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Form Step — select a form:</p>
                <FormList ownerId={ownerId} onCreateNew={() => {}} onSelect={handleFormClick} useDtos={false} formListCount={0} />
              </div>
            </div>
          )}
        </div>

        {/* Edge Routing — visible once 2+ steps exist; Add Edge form is gated on nodes having server-assigned IDs */}
        {nodeTemplates.length >= 2 && (
          <div className="bg-gray-100 p-4 mt-6 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-700">Edge Routing</h3>
                <button
                  type="button"
                  onClick={() => setEdgeHelpOpen((o) => !o)}
                  aria-label="What is edge routing?"
                  className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-300 text-gray-600 text-xs font-bold hover:bg-blue-100 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  ?
                </button>
              </div>
              <button
                type="button"
                onClick={() => setEdgesCollapsed((c) => !c)}
                className="group p-1"
                aria-label={edgesCollapsed ? 'Expand edge routing' : 'Collapse edge routing'}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-transform duration-200 ${edgesCollapsed ? '-rotate-90' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            {edgeHelpOpen && (
              <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-gray-700 space-y-2">
                <p className="font-semibold text-blue-700">What is Edge Routing?</p>
                <p>
                  By default, your workflow runs steps in order — Step 1, then Step 2, and so on. <strong>Edges</strong> let you change that behaviour by defining explicit paths between steps.
                </p>
                <p>
                  Each edge connects a <strong>From</strong> step to a <strong>To</strong> step. You can optionally attach a <strong>condition</strong> (written as JSON) that must be true for the engine to follow that edge — for example, only proceed to the approval step if the reviewer marked the submission as approved.
                </p>
                <p>
                  Edges with no condition are <strong>unconditional</strong> — the engine always follows them. When multiple edges leave the same step, the one with the lowest <strong>priority</strong> number is tried first.
                </p>
                <p className="text-gray-500 text-xs">
                  If no edges are defined, the workflow falls back to sequential step order.
                </p>
                <button
                  type="button"
                  onClick={() => setEdgeHelpOpen(false)}
                  className="mt-1 text-xs font-medium text-blue-600 hover:underline focus:outline-none"
                >
                  Got it
                </button>
              </div>
            )}
            {!edgesCollapsed && (
              <div className="space-y-4 mt-4">
                {edgeTemplates.length === 0 ? (
              <p className="text-sm text-gray-500">
                No edges defined. Without edges the engine advances steps by order.
              </p>
            ) : (
              <div className="space-y-2">
                {edgeTemplates.map((edge) => {
                  const fromNode = nodeTemplates.find((n) => n.id === edge.fromNodeTemplateId);
                  const toNode = nodeTemplates.find((n) => n.id === edge.toNodeTemplateId);
                  const isEditing = edge.id === editingEdgeId;

                  if (isEditing) {
                    return (
                      <div key={edge.id} className="border border-blue-300 rounded-lg p-3 bg-blue-50 space-y-3">
                        <p className="text-sm font-medium text-blue-700">Edit Edge</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">From Step</label>
                            <select
                              value={editFields.from}
                              onChange={(e) => setEditFields((f) => ({ ...f, from: e.target.value }))}
                              className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline"
                            >
                              {assignedNodes
                                .sort((a, b) => a.order - b.order)
                                .map((n) => (
                                  <option key={n.id} value={n.id}>
                                    Step {n.order} — {n.nodeType}
                                  </option>
                                ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">To Step</label>
                            <select
                              value={editFields.to}
                              onChange={(e) => setEditFields((f) => ({ ...f, to: e.target.value }))}
                              className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline"
                            >
                              {assignedNodes
                                .sort((a, b) => a.order - b.order)
                                .map((n) => (
                                  <option key={n.id} value={n.id}>
                                    Step {n.order} — {n.nodeType}
                                  </option>
                                ))}
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Name (optional)</label>
                            <input
                              type="text"
                              value={editFields.name}
                              onChange={(e) => setEditFields((f) => ({ ...f, name: e.target.value }))}
                              placeholder="e.g. Approved path"
                              className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Priority (lower = first)</label>
                            <input
                              type="number"
                              value={editFields.priority}
                              onChange={(e) => setEditFields((f) => ({ ...f, priority: Number(e.target.value) }))}
                              className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Condition JSON (optional — leave blank for unconditional)
                          </label>
                          <textarea
                            value={editFields.conditionJson}
                            onChange={(e) => setEditFields((f) => ({ ...f, conditionJson: e.target.value }))}
                            placeholder={'{"field":"decision","operator":"eq","value":"approved"}'}
                            rows={3}
                            className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 text-sm font-mono leading-tight focus:outline-none focus:shadow-outline"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleEditSave(edge.id)}
                            disabled={!editFields.from || !editFields.to || editFields.from === editFields.to}
                            className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={handleEditCancel}
                            className="inline-flex items-center justify-center rounded-lg bg-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={edge.id} className="flex items-start justify-between border border-gray-300 rounded-lg p-3 bg-white">
                      <div className="text-sm">
                        <span className="font-medium text-gray-800">
                          Step {fromNode?.order ?? '?'} → Step {toNode?.order ?? '?'}
                        </span>
                        {edge.name && <span className="ml-2 text-gray-600">({edge.name})</span>}
                        <span className="ml-2 text-gray-400 text-xs">Priority: {edge.priority}</span>
                        <span className={
                          summarizeCondition(edge.conditionJson) === 'Unconditional'
                            ? 'mt-0.5 block text-xs text-gray-400 italic'
                            : 'mt-0.5 block text-xs text-gray-600 bg-gray-50 rounded px-1.5 py-0.5'
                        }>
                          {summarizeCondition(edge.conditionJson)}
                        </span>
                      </div>
                      <div className="flex gap-2 shrink-0 ml-2">
                        <button
                          type="button"
                          onClick={() => handleEditStart(edge)}
                          disabled={!!editingEdgeId}
                          className="text-blue-400 hover:text-blue-600 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteEdge(edge.id)}
                          disabled={!!editingEdgeId}
                          className="text-red-400 hover:text-red-600 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {assignedNodes.length >= 2 ? (
              <div className={`border border-dashed border-gray-300 rounded-lg p-4 space-y-3 bg-white${editingEdgeId ? ' opacity-50 pointer-events-none' : ''}`}>
                <p className="text-sm font-medium text-gray-700">Add Edge</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">From Step</label>
                    <select
                      value={newEdgeFrom}
                      onChange={(e) => { void handleFromStepChange(e.target.value); }}
                      className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline"
                    >
                      <option value="">Select step</option>
                      {assignedNodes
                        .sort((a, b) => a.order - b.order)
                        .map((n) => (
                          <option key={n.id} value={n.id}>
                            Step {n.order} — {n.nodeType}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">To Step</label>
                    <select
                      value={newEdgeTo}
                      onChange={(e) => setNewEdgeTo(e.target.value)}
                      className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline"
                    >
                      <option value="">Select step</option>
                      {assignedNodes
                        .sort((a, b) => a.order - b.order)
                        .map((n) => (
                          <option key={n.id} value={n.id}>
                            Step {n.order} — {n.nodeType}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Name (optional)</label>
                    <input
                      type="text"
                      value={newEdgeName}
                      onChange={(e) => setNewEdgeName(e.target.value)}
                      placeholder="e.g. Approved path"
                      className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Priority (lower = first)</label>
                    <input
                      type="number"
                      value={newEdgePriority}
                      onChange={(e) => setNewEdgePriority(Number(e.target.value))}
                      className="shadow appearance-none border rounded w-full py-1.5 px-2 text-gray-700 text-sm leading-tight focus:outline-none focus:shadow-outline"
                    />
                  </div>
                </div>
                <div>
                  {conditionFieldsLoading ? (
                    <p className="text-xs text-gray-400">Loading fields…</p>
                  ) : newEdgeFrom ? (
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Condition (optional)</label>
                      <EdgeConditionBuilder
                        availableFields={conditionFields}
                        value={newEdgeCondition}
                        onChange={setNewEdgeCondition}
                      />
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">Select a From Step to configure a condition.</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleAddEdge}
                  disabled={!newEdgeFrom || !newEdgeTo || newEdgeFrom === newEdgeTo}
                  className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Add Edge
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-400">
                At least 2 saved steps are required to define edges.
              </p>
            )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowCreator;